import { useCallback, useMemo } from 'react';
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { DEFAULT_PAGE_SIZE } from '@wit-support/ui';
import { ADS_CAMPAIGN_SORTS } from '@wit-support/admin-contracts';
import { CAMPAIGN_TABS, DEFAULT_TAB, isDay, } from './view.js';
/*
 * Кабинет, фильтры и страница — в адресе (контракт A2a): ссылкой можно
 * поделиться, «назад» возвращает прежнее. Читается СЫРАЯ строка адреса из
 * истории, а не разобранный `search` роутера (даже `location.searchStr` у
 * роутера пересобран из разбора): роутер превращает цифровые значения в числа
 * (id кампании Meta — 18 цифр, он теряет точность и портится), а строки из
 * цифр, которые он сам записывает, берёт в кавычки — они снимаются. Мусор
 * значит «по умолчанию».
 */
/** Размеры страницы: потолок сервера — 100 (контракт A1), «200» кита не предлагаем. */
export const PAGE_SIZES = [25, 50, 100];
/** Адрес → `{ параметр: строка }`; кавычки, которыми роутер обрамляет цифровые строки, снимаются. */
export function rawSearchOf(searchStr) {
    const out = {};
    for (const [k, v] of new URLSearchParams(searchStr)) {
        out[k] = v.length >= 2 && v.startsWith('"') && v.endsWith('"') ? v.slice(1, -1) : v;
    }
    return out;
}
const str = (v) => typeof v === 'string' && v.trim() !== ''
    ? v
    : typeof v === 'number' && Number.isFinite(v)
        ? String(v)
        : undefined;
export function applyPatch(prev, patch) {
    const next = prev !== null && typeof prev === 'object' ? { ...prev } : {};
    for (const [k, v] of Object.entries(patch)) {
        if (v === undefined || v === null || v === '' || v === false)
            delete next[k];
        else
            next[k] = v;
    }
    return next;
}
const posInt = (v, d) => {
    const n = Number(v);
    return Number.isInteger(n) && n >= 1 ? n : d;
};
export function useCampaignsUrl() {
    // подписка на смену адреса; сырая строка — из истории (см. `rawSearchOf`)
    const href = useRouterState({ select: (st) => st.location.href });
    const router = useRouter();
    const navigate = useNavigate();
    const patch = useCallback((p, opts) => {
        void navigate({
            to: '.',
            search: (prev) => {
                const next = applyPatch(prev, p);
                // Длинные цифровые значения (`q` с id кампании, кабинет из цифр)
                // роутер разбирает в число и теряет цифры — пишем назад из сырой
                // строки, потерянное число в адрес не попадает.
                const raw = rawSearchOf(router.history.location.search);
                for (const [k, v] of Object.entries(raw)) {
                    if (!(k in p) && /^\d{16,}$/.test(v))
                        next[k] = v;
                }
                return next;
            },
            ...(opts?.replace ? { replace: true } : {}),
        });
    }, [navigate, router]);
    // href в зависимостях useMemo: идентичность роутера не меняется, а адрес — да
    return useMemo(() => {
        const r = rawSearchOf(router.history.location.search);
        const tab = r['tab'];
        const sortKey = r['sort'];
        const size = posInt(r['pageSize'], DEFAULT_PAGE_SIZE);
        return {
            account: /^[a-f0-9]{24}$/.test(r['account'] ?? '') ? r['account'] : undefined,
            page: posInt(r['page'], 1),
            pageSize: PAGE_SIZES.includes(size) ? size : DEFAULT_PAGE_SIZE,
            q: str(r['q']) ?? '',
            tab: CAMPAIGN_TABS.includes(tab ?? '')
                ? tab
                : DEFAULT_TAB,
            spent7d: r['spent7d'] === '1' || r['spent7d'] === 'true',
            sort: sortKey &&
                sortKey !== 'default' &&
                ADS_CAMPAIGN_SORTS.includes(sortKey)
                ? { key: sortKey, dir: r['dir'] === 'asc' ? 'asc' : 'desc' }
                : null,
            day: isDay(r['day']) ? r['day'] : undefined,
            grad: r['grad'] === '1' || r['grad'] === 'true',
            patch,
        };
    }, [href, router, patch]);
}
//# sourceMappingURL=url-state.js.map